import { useState } from "react";
import styles from "./FeedbackBlock.module.scss";

const FeedbackBlock = () => {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const ratingData: { [key: string]: number } = {
    Ausgezeichnet: 100,
    "Sehr gut": 0,
    Durchschnittlich: 0,
    Schlecht: 0,
    "Sehr schlecht": 0,
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { [key: string]: string } = {};

    // Validate required fields
    if (rating === 0) newErrors.rating = "Please select a rating.";
    if (feedback.trim() === "")
      newErrors.feedback = "Feedback cannot be empty.";
    if (name.trim() === "") newErrors.name = "Name is required.";
    if (email.trim() === "") newErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(email))
      newErrors.email = "Please enter a valid email.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors); // Set error messages
    } else {
      setIsLoading(true); // Show loading state
      setSuccessMessage(""); // Reset success message on new submit

      // Simulate a successful form submission (e.g., sending data to a server)
      setTimeout(() => {
        setIsLoading(false); // Hide loading state
        setSuccessMessage(
          "Your review has been submitted and is pending approval."
        ); // Show success message

        // Reset the form fields after successful submission
        setRating(0);
        setFeedback("");
        setName("");
        setEmail("");
        setErrors({}); // Clear errors
      }, 2000);
    }
  };

  // Handler to clear specific errors as the user types
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    const { value } = e.target;
    if (value.trim() !== "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "", // Clear the error message for the respective field
      }));
    }
  };

  return (
    <div className="container section">
      <div className="sectionContainer sectionContainerWhite platformCont">
        <div className={styles.formAndStats}>
          <div className={styles.leftBlock}>
            <div className={styles.feedbackHeader}>
              <p className={styles.inputLabel}>Rating</p>
              <div className={styles.rating}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <img
                    key={star}
                    src={
                      star <= rating
                        ? "/images/star-full.svg"
                        : "/images/star-empty.svg"
                    }
                    alt={`star-${star}`}
                    className={styles.star}
                    onClick={() => setRating(star)}
                  />
                ))}
              </div>
              {errors.rating && <p className={styles.error}>{errors.rating}</p>}
            </div>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputGroup}>
                <p className={styles.inputLabel}>Wie war Ihre Erfahrung?</p>
                <textarea
                  placeholder="Teilen Sie Ihre Bewertung mit anderen"
                  value={feedback}
                  onChange={(e) => {
                    setFeedback(e.target.value);
                    handleInputChange(e, "feedback"); // Clear feedback error as user types
                  }}
                  className={styles.textarea}
                />
                {errors.feedback && (
                  <p className={styles.error}>{errors.feedback}</p>
                )}
              </div>

              <div className={styles.inputGroup}>
                <p className={styles.inputLabel}>Name</p>
                <input
                  type="text"
                  placeholder="Geben Sie Ihren Namen ein"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    handleInputChange(e, "name"); // Clear name error as user types
                  }}
                  className={styles.input}
                />
                {errors.name && <p className={styles.error}>{errors.name}</p>}
              </div>

              <div className={styles.inputGroup}>
                <p className={styles.inputLabel}>Ihre E-Mail-Adresse</p>
                <input
                  type="email"
                  placeholder="Geben Sie Ihre E-Mail-Adresse ein"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    handleInputChange(e, "email"); // Clear email error as user types
                  }}
                  className={styles.input}
                />
                {errors.email && <p className={styles.error}>{errors.email}</p>}
              </div>

              <button
                type="submit"
                className={`${styles.submitBtn} ${
                  isLoading ? styles.loading : ""
                }`}
              >
                {isLoading ? "Sending..." : "Senden"}
              </button>
            </form>
          </div>
          <div className={styles.ratingSummary}>
            <div className={styles.ratingBarContainer}>
              <div className={styles.ratingLine}>
                <span className={styles.ratingText}>5,0</span>
                <div className={styles.ratingStars}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <img
                      key={star}
                      src="/images/star-full.svg" // Full star image
                      alt={`full-star-${star}`}
                      className={styles.star}
                    />
                  ))}
                </div>
              </div>

              <p className={styles.statsSubtitle}>
                5,0 von 5 Sternen (basierend auf 5 Bewertungen)
              </p>
              {Object.keys(ratingData).map((key: keyof typeof ratingData) => (
                <div key={key} className={styles.ratingCategory}>
                  <div className={styles.ratingCategoryText}>
                    <span>{key}</span>
                  </div>
                  <div className={styles.ratingBarWrapper}>
                    <div className={styles.ratingBar}>
                      <div
                        className={styles.filled}
                        style={{ width: `${ratingData[key]}%` }}
                      />
                    </div>
                  </div>
                  <span className={styles.percentageText}>
                    {ratingData[key]}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {successMessage && (
          <div className={styles.successMessage}>{successMessage}</div>
        )}
      </div>
    </div>
  );
};

export default FeedbackBlock;
